// components/common/Badge/StatusBadge.tsx
import { ReactElement } from 'react';
import { Box, Typography, BoxProps } from '@mui/material';
import { styled, alpha, SxProps, Theme } from '@mui/material/styles';

// Status types
export type TableStatus =
  | 'playing'
  | 'finished'
  | 'draw'
  | 'oneNoShow'
  | 'bothNoShow'
  | 'appealOpen'
  | 'appealProgress'
  | 'dropOut'
  | 'callInProgress'
  | 'stillPlaying'
  | 'noPlayers'
  | 'missing'
  | 'featured'
  | 'hasComment';

export type GenericStatus =
  | 'active'
  | 'inactive'
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'open'
  | 'closed'
  | 'inProgress';

export type AppealJudgement = 'initial_correct' | 'overturned';

export type BreakStatus = 'onBreak' | 'available';

export type StatusType = TableStatus | GenericStatus | AppealJudgement | BreakStatus | string;

interface StatusConfigItem {
  color: string;
  label: string;
}

// Status configuration
const statusConfig: Record<string, StatusConfigItem> = {
  // Table statuses
  playing: { color: '#00FF88', label: 'Playing' },
  finished: { color: '#8892A0', label: 'Finished' },
  draw: { color: '#00FFD4', label: 'Draw' },
  oneNoShow: { color: '#FF6B35', label: 'No Show' },
  bothNoShow: { color: '#FF3366', label: 'Both No Show' },
  appealOpen: { color: '#FFE600', label: 'Appeal' },
  appealProgress: { color: '#7B68EE', label: 'In Progress' },
  dropOut: { color: '#FF3366', label: 'Drop' },
  callInProgress: { color: '#FFE600', label: 'Call' },
  stillPlaying: { color: '#00FF88', label: 'Playing' },
  noPlayers: { color: '#FF3366', label: 'No Players' },
  missing: { color: '#7B68EE', label: 'Missing' },
  featured: { color: '#00D4FF', label: 'Featured' },
  hasComment: { color: '#FF8C42', label: 'Comment' },

  // Generic statuses
  active: { color: '#00FF88', label: 'Active' },
  inactive: { color: '#8892A0', label: 'Inactive' },
  pending: { color: '#FFE600', label: 'Pending' },
  approved: { color: '#00FF88', label: 'Approved' },
  rejected: { color: '#FF3366', label: 'Rejected' },
  open: { color: '#FFE600', label: 'Open' },
  closed: { color: '#8892A0', label: 'Closed' },
  inProgress: { color: '#7B68EE', label: 'In Progress' },

  // Appeal judgements
  initial_correct: { color: '#00FF88', label: 'Upheld' },
  overturned: { color: '#FF3366', label: 'Overturned' },

  // Break status
  onBreak: { color: '#FF3366', label: 'On Break' },
  available: { color: '#00FF88', label: 'Available' },
};

interface StyledBadgeProps {
  badgeColor: string;
  pulse?: boolean;
  size?: 'small' | 'medium';
}

const StyledBadge = styled(Box, {
  shouldForwardProp: (prop) => !['badgeColor', 'pulse', 'size'].includes(prop as string),
})<StyledBadgeProps>(({ badgeColor, pulse, size }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: size === 'small' ? '2px 8px' : '4px 12px',
  borderRadius: '9999px',
  backgroundColor: alpha(badgeColor, 0.15),
  border: `1px solid ${badgeColor}`,
  transition: 'all 0.3s ease',

  ...(pulse && {
    animation: 'pulse-border 2s ease-in-out infinite',
  }),
}));

interface DotProps {
  dotColor: string;
  pulse?: boolean;
}

const Dot = styled(Box, {
  shouldForwardProp: (prop) => !['dotColor', 'pulse'].includes(prop as string),
})<DotProps>(({ dotColor, pulse }) => ({
  width: '6px',
  height: '6px',
  borderRadius: '50%',
  backgroundColor: dotColor,

  ...(pulse && {
    animation: 'pulse-glow 2s ease-in-out infinite',
  }),
}));

export interface StatusBadgeProps extends Omit<BoxProps, 'color'> {
  status: StatusType;
  label?: string;
  color?: string;
  pulse?: boolean;
  showDot?: boolean;
  size?: 'small' | 'medium';
  sx?: SxProps<Theme>;
}

/**
 * StatusBadge - Display status with color-coded badge
 */
export function StatusBadge({
  status,
  label,
  color,
  pulse = false,
  showDot = true,
  size = 'medium',
  sx = {},
  ...props
}: StatusBadgeProps): ReactElement {
  const config = statusConfig[status] || { color: '#8892A0', label: status };
  const badgeColor = color || config.color;
  const badgeLabel = label || config.label;

  return (
    <StyledBadge
      badgeColor={badgeColor}
      pulse={pulse}
      size={size}
      sx={sx}
      {...props}
    >
      {showDot && <Dot dotColor={badgeColor} pulse={pulse} />}
      <Typography
        sx={{
          fontFamily: '"Share Tech Mono", monospace',
          fontSize: size === 'small' ? '0.65rem' : '0.75rem',
          color: badgeColor,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontWeight: 500,
        }}
      >
        {badgeLabel}
      </Typography>
    </StyledBadge>
  );
}

// Role types
export type StaffRoleType =
  | 'admin'
  | 'Manager'
  | 'Head Judge'
  | 'Scorekeeper'
  | 'Shield'
  | 'Team Lead'
  | 'Floor Judge'
  | 'Feature Match Judge'
  | 'pending';

// Role configuration
const roleConfig: Record<string, StatusConfigItem> = {
  admin: { color: '#FF3366', label: 'Admin' },
  Manager: { color: '#FF6B35', label: 'Manager' },
  'Head Judge': { color: '#FFE600', label: 'Head Judge' },
  Scorekeeper: { color: '#00FFD4', label: 'Scorekeeper' },
  Shield: { color: '#7B68EE', label: 'Shield' },
  'Team Lead': { color: '#00FF88', label: 'Team Lead' },
  'Floor Judge': { color: '#8892A0', label: 'Floor Judge' },
  'Feature Match Judge': { color: '#4A5568', label: 'FM Judge' },
  pending: { color: '#FFE600', label: 'Pending' },
};

export interface RoleBadgeProps extends Omit<BoxProps, 'color'> {
  role: StaffRoleType | string;
  size?: 'small' | 'medium';
  sx?: SxProps<Theme>;
}

/**
 * RoleBadge - Display user role with color-coded badge
 */
export function RoleBadge({ role, size = 'medium', sx = {}, ...props }: RoleBadgeProps): ReactElement {
  const config = roleConfig[role] || { color: '#8892A0', label: role };

  return (
    <StatusBadge
      status={role}
      color={config.color}
      label={config.label}
      showDot={false}
      size={size}
      sx={sx}
      {...props}
    />
  );
}

export interface LiveBadgeProps extends BoxProps {
  sx?: SxProps<Theme>;
}

/**
 * LiveBadge - Special badge for live indicators
 */
export function LiveBadge({ sx = {}, ...props }: LiveBadgeProps): ReactElement {
  return (
    <StyledBadge
      badgeColor="#FF3366"
      pulse
      size="small"
      sx={{
        backgroundColor: 'rgba(255, 51, 102, 0.2)',
        ...sx,
      }}
      {...props}
    >
      <Dot dotColor="#FF3366" pulse />
      <Typography
        sx={{
          fontFamily: '"Share Tech Mono", monospace',
          fontSize: '0.65rem',
          color: '#FF3366',
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          fontWeight: 600,
        }}
      >
        LIVE
      </Typography>
    </StyledBadge>
  );
}

export default StatusBadge;
